// src/admin/components/HomePageConfig.jsx
import React, { useEffect, useState, useCallback } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./HomePageConfig.css";
import { CONFIG } from "../../config";

// Import components
import { DEFAULT_FONT_STYLES } from "./FontStyleControls";
import CustomerReviewSection from "./HomePageConfig/CustomerReviewSection";
import RegularSection from "./HomePageConfig/RegularSection";
import HowWeWorkSection from "./HomePageConfig/HowWeWorkSection";
// Import new modular components
import FontStylesConfiguration from "./HomePageConfig/FontStylesConfiguration";
import LayoutSettings from "./HomePageConfig/LayoutSettings";
import InstantQuoteForm from "./HomePageConfig/InstantQuoteForm";
import AboutUsSection from "./HomePageConfig/AboutUsSection";
import ServicesSectionHeader from "./HomePageConfig/ServicesSectionHeader";
import SectionStyleCard from "./HomePageConfig/SectionStyleCard";

/** ---- Config ---- */
const UPLOAD_URL = CONFIG.UPLOAD_URL;
const IMAGES_BASE = CONFIG.IMAGES_BASE;

// --------- helpers ----------
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const normalizeList = (arr) =>
  Array.isArray(arr)
    ? arr.map((x) => (x && x._id ? x : { _id: uid(), ...x }))
    : [];

// ----- main -----
// src/admin/components/HomePageConfig.jsx
// ... (imports remain the same)

export default function HomePageConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Hero Section
  const [hero, setHero] = useState([]);
  const [heroColors, setHeroColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    buttonTextColor: "#ffffff",
    buttonBgColor: "#000000",
  });
  const [heroSettings, setHeroSettings] = useState({
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });

  // Customer Reviews
  const [customerReview, setCustomerReview] = useState({
    image: "",
    backgroundColor: "#ffffff",
    overlayOpacity: 0.3,
    reviews: [],
    sectionTitle: "Customer Reviews",
    sectionDescription: "What our customers say about us",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [customerReviewColors, setCustomerReviewColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    reviewerNameColor: "#000000",
    reviewerDesignationColor: "#666666",
    ratingColor: "#FFD700",
  });

  // How We Work (replaces Why Choose Us)
  const [howWeWork, setHowWeWork] = useState({
    display: true,
    sectionTitle: "HOW WE WORK",
    sectionSubtitle: "At Regency Interior Design Company, we believe that the journey of transforming a space should be as enjoyable as the end result. Here's how we bring your vision to life:",
    backgroundColor: "#111111",
    titleColor: "#d4af37",
    subtitleColor: "rgba(255, 255, 255, 0.8)",
    cardBackground: "#ffffff",
    numberColor: "#d4af37",
    textColor: "#111111",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
    steps: [
      { no: "01", text: "Touch and Feel our Product" },
      { no: "02", text: "Get a free personalised concept and estimate" },
      { no: "03", text: "10% Token advance" },
      { no: "04", text: "Detailed Design" },
      { no: "05", text: "Design finalization with 40% payment" },
      { no: "06", text: "Production" },
      { no: "07", text: "45% Payment and Dispatch" },
      { no: "08", text: "Installation" },
      { no: "09", text: "Site Inspection" },
      { no: "10", text: "Handover with 5% Final Payment" },
      { no: "11", text: "Enjoy your Dream Home" },
      { no: "12", text: "Service" },
    ]
  });
  
  const [howWeWorkColors, setHowWeWorkColors] = useState({
    headingColor: "#d4af37",
    subHeadingColor: "rgba(255, 255, 255, 0.8)",
    contentColor: "#111111",
    numberColor: "#d4af37",
    cardBgColor: "#ffffff",
  });

  // Services
  const [services, setServices] = useState([]);
  const [servicesColors, setServicesColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    serviceTitleColor: "#000000",
    serviceContentColor: "#666666",
  });
  const [servicesSettings, setServicesSettings] = useState({
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });

  // Forms list and display settings
  const [availableForms, setAvailableForms] = useState([]);
  const [instantQuoteForm, setInstantQuoteForm] = useState({
    display: false,
    formId: "",
    position: "afterAboutUs",
    sectionTitle: "Get Instant Quotes",
    sectionDescription: "Fill out the form below to get a free quote",
    backgroundImage: "",
    backgroundColor: "",
    overlayOpacity: 0.5,
    titleColor: "#000000",
    descriptionColor: "#333333",
    formBgColor: "#ffffff",
    formTextColor: "#333333",
    buttonColor: "#ffffff",
    buttonBgColor: "#000000",
    buttonHoverColor: "#ffffff",
    buttonHoverBgColor: "#333333",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
    sideContent: {
      enabled: true,
      headingTop: "WHY CHOOSE",
      headingHighlight: "REGENCY INTERIOR",
      bullets: [
        "Custom furniture and interiors tailored to your needs.",
        "Durable woodwork with marine ply and natural wood.",
        "Premium hardware from Hettich, Blum, and Godrej.",
        "Smart storage with stainless steel and tandem baskets.",
        "Expert renovation services for modern spaces.",
      ],
      footerLine: "REACH OUT TODAY TO DISCUSS YOUR VISION!",
      headingColor: "#000000",
      highlightColor: "#d4af37",
      bulletColor: "#333333",
      bulletIconColor: "#d4af37",
      footerColor: "#000000",
      useSectionColors: true,
    },
  });

  // Services section header
  const [servicesSection, setServicesSection] = useState({
    title: "",
    subtitle: "",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [servicesSectionColors, setServicesSectionColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
  });

  // Nav-driven sections (Design Ideas / Categories / Latest / Updates)
  const [specialCategorySection, setSpecialCategorySection] = useState({
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [specialCategoryColors, setSpecialCategoryColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
  });

  const [categorySection, setCategorySection] = useState({
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [categoryColors, setCategoryColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
  });

  const [latestSection, setLatestSection] = useState({
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [latestColors, setLatestColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
  });

  const [updatesSection, setUpdatesSection] = useState({
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [updatesColors, setUpdatesColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    buttonTextColor: "#000000",
  });

  // About Us State
  const [aboutUs, setAboutUs] = useState({
    display: true,
    title: "",
    subtitle: "",
    description: "",
    image: "",
    yearsExperience: "",
    projectsCompleted: "",
    buttonText: "",
    buttonLink: "",
    backgroundColor: "#ffffff",
    useSectionColors: true,
    useSectionFontSizes: false,
    fontSizes: {},
  });
  const [aboutUsColors, setAboutUsColors] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#666666",
    statsColor: "#000000",
    buttonTextColor: "#ffffff",
    buttonBgColor: "#000000",
  });

  // Font Styles Configuration - CENTRALIZED
  const [fontStyles, setFontStyles] = useState(DEFAULT_FONT_STYLES);

  const [layoutOrder, setLayoutOrder] = useState({
    hero: 1,
    aboutUs: 2,
    customerReviews: 3,
    howWeWork: 4, // Changed from whyChooseUs
    specialCategory: 5,
    categories: 6,
    latest: 7,
    updates: 8,
    services: 9,
    instantQuote: 10,
  });

  const [backgroundColor, setBackgroundColor] = useState("#ffffff");

  // Load config and available forms
  useEffect(() => {
    const loadData = async () => {
      try {
        const homepageSnap = await get(ref(db, "homepageconfig"));
        if (homepageSnap.exists()) {
          const v = homepageSnap.val() || {};
          
          // Load hero with proper defaults
          const heroData = v.hero || [];
          const normalizedHero = normalizeList(heroData.map(item => ({
            ...item,
            contentAlignment: item.contentAlignment || "left",
            titleColor: item.titleColor || "#ffffff",
            contentColor: item.contentColor || "#ffffff",
            buttonColor: item.buttonColor || "#ffffff",
            buttonBgColor: item.buttonBgColor || "#000000",
            buttonHoverColor: item.buttonHoverColor || "#ffffff",
            buttonHoverBgColor: item.buttonHoverBgColor || "#333333",
            overlayOpacity: item.overlayOpacity ?? 0.3
          })));
          setHero(normalizedHero);
          
          // Load hero colors
          setHeroColors(v.heroColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
            buttonTextColor: "#ffffff",
            buttonBgColor: "#000000",
          });
          
          // Load hero settings
          setHeroSettings({
            useSectionColors: v?.heroSettings?.useSectionColors !== false,
            useSectionFontSizes: v?.heroSettings?.useSectionFontSizes === true,
            fontSizes: v?.heroSettings?.fontSizes || {},
          });
          
          // Load customer reviews with new structure
          const reviewData = v.customerreview || {};
          setCustomerReview({
            image: reviewData.image || "",
            backgroundColor: reviewData.backgroundColor || "#ffffff",
            overlayOpacity: reviewData.overlayOpacity ?? 0.3,
            reviews: normalizeList(reviewData.reviews || []).map(review => ({
              ...review,
              rating: review.rating || "5",
              designation: review.designation || "",
              description: review.description || "",
            })),
            sectionTitle: reviewData.sectionTitle || "Customer Reviews",
            sectionDescription: reviewData.sectionDescription || "What our customers say about us",
            useSectionColors: reviewData.useSectionColors !== false,
            useSectionFontSizes: reviewData.useSectionFontSizes === true,
            fontSizes: reviewData.fontSizes || {},
          });
          
          // Load customer review colors
          setCustomerReviewColors(v.customerReviewColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
            reviewerNameColor: "#000000",
            reviewerDesignationColor: "#666666",
            ratingColor: "#FFD700",
          });
          
          // Load How We Work (replaces Why Choose Us)
          const howWeWorkData = v.howWeWork || {};
          setHowWeWork({
            display: howWeWorkData.display ?? true,
            sectionTitle: howWeWorkData.sectionTitle || "HOW WE WORK",
            sectionSubtitle: howWeWorkData.sectionSubtitle || "At Regency Interior Design Company, we believe that the journey of transforming a space should be as enjoyable as the end result. Here's how we bring your vision to life:",
            backgroundColor: howWeWorkData.backgroundColor || "#111111",
            titleColor: howWeWorkData.titleColor || "#d4af37",
            subtitleColor: howWeWorkData.subtitleColor || "rgba(255, 255, 255, 0.8)",
            cardBackground: howWeWorkData.cardBackground || "#ffffff",
            numberColor: howWeWorkData.numberColor || "#d4af37",
            textColor: howWeWorkData.textColor || "#111111",
            useSectionColors: howWeWorkData.useSectionColors !== false,
            useSectionFontSizes: howWeWorkData.useSectionFontSizes === true,
            fontSizes: howWeWorkData.fontSizes || {},
            steps: Array.isArray(howWeWorkData.steps) && howWeWorkData.steps.length > 0 
              ? howWeWorkData.steps 
              : [
                  { no: "01", text: "Touch and Feel our Product" },
                  { no: "02", text: "Get a free personalised concept and estimate" },
                  { no: "03", text: "10% Token advance" },
                  { no: "04", text: "Detailed Design" },
                  { no: "05", text: "Design finalization with 40% payment" },
                  { no: "06", text: "Production" },
                  { no: "07", text: "45% Payment and Dispatch" },
                  { no: "08", text: "Installation" },
                  { no: "09", text: "Site Inspection" },
                  { no: "10", text: "Handover with 5% Final Payment" },
                  { no: "11", text: "Enjoy your Dream Home" },
                  { no: "12", text: "Service" },
                ]
          });
          
          // Load how we work colors
          setHowWeWorkColors(v.howWeWorkColors || {
            headingColor: "#d4af37",
            subHeadingColor: "rgba(255, 255, 255, 0.8)",
            contentColor: "#111111",
            numberColor: "#d4af37",
            cardBgColor: "#ffffff",
          });
          
          // Load Services
          setServices(normalizeList(v.services || []));
          
          // Load services colors
          setServicesColors(v.servicesColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            serviceTitleColor: "#000000",
            serviceContentColor: "#666666",
          });
          
          // Load services settings
          setServicesSettings({
            useSectionColors: v?.servicesSettings?.useSectionColors !== false,
            useSectionFontSizes: v?.servicesSettings?.useSectionFontSizes === true,
            fontSizes: v?.servicesSettings?.fontSizes || {},
          });
          
          // Load Services Section Header
          setServicesSection({
            title: v?.servicesSection?.title || "",
            subtitle: v?.servicesSection?.subtitle || "",
            useSectionColors: v?.servicesSection?.useSectionColors !== false,
            useSectionFontSizes: v?.servicesSection?.useSectionFontSizes === true,
            fontSizes: v?.servicesSection?.fontSizes || {},
          });
          
          // Load services section colors
          setServicesSectionColors(v.servicesSectionColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
          });

          // Load nav-driven section styles
          setSpecialCategorySection({
            useSectionColors: v?.specialCategorySection?.useSectionColors !== false,
            useSectionFontSizes: v?.specialCategorySection?.useSectionFontSizes === true,
            fontSizes: v?.specialCategorySection?.fontSizes || {},
          });
          setSpecialCategoryColors(v.specialCategoryColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
          });

          setCategorySection({
            useSectionColors: v?.categorySection?.useSectionColors !== false,
            useSectionFontSizes: v?.categorySection?.useSectionFontSizes === true,
            fontSizes: v?.categorySection?.fontSizes || {},
          });
          setCategoryColors(v.categoryColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
          });

          setLatestSection({
            useSectionColors: v?.latestSection?.useSectionColors !== false,
            useSectionFontSizes: v?.latestSection?.useSectionFontSizes === true,
            fontSizes: v?.latestSection?.fontSizes || {},
          });
          setLatestColors(v.latestColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
          });

          setUpdatesSection({
            useSectionColors: v?.updatesSection?.useSectionColors !== false,
            useSectionFontSizes: v?.updatesSection?.useSectionFontSizes === true,
            fontSizes: v?.updatesSection?.fontSizes || {},
          });
          setUpdatesColors(v.updatesColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
            buttonTextColor: "#000000",
          });

          // Load About Us
          setAboutUs({
            display: v?.aboutUs?.display ?? true,
            title: v?.aboutUs?.title || "",
            subtitle: v?.aboutUs?.subtitle || "",
            description: v?.aboutUs?.description || "",
            image: v?.aboutUs?.image || "",
            yearsExperience: v?.aboutUs?.yearsExperience || "",
            projectsCompleted: v?.aboutUs?.projectsCompleted || "",
            buttonText: v?.aboutUs?.buttonText || "",
            buttonLink: v?.aboutUs?.buttonLink || "",
            backgroundColor: v?.aboutUs?.backgroundColor || "#ffffff",
            useSectionColors: v?.aboutUs?.useSectionColors !== false,
            useSectionFontSizes: v?.aboutUs?.useSectionFontSizes === true,
            fontSizes: v?.aboutUs?.fontSizes || {},
          });
          
          // Load about us colors
          setAboutUsColors(v.aboutUsColors || {
            headingColor: "#000000",
            subHeadingColor: "#333333",
            contentColor: "#666666",
            statsColor: "#000000",
            buttonTextColor: "#ffffff",
            buttonBgColor: "#000000",
          });

          // Load instant quote form settings
          const formData = v?.instantQuoteForm || {};
          setInstantQuoteForm({
            display: formData.display ?? false,
            formId: formData.formId || "",
            position: formData.position || "afterAboutUs",
            sectionTitle: formData.sectionTitle || "Get Instant Quotes",
            sectionDescription: formData.sectionDescription || "Fill out the form below to get a free quote",
            backgroundImage: formData.backgroundImage || "",
            backgroundColor: formData.backgroundColor || "",
            overlayOpacity: formData.overlayOpacity ?? 0.5,
            titleColor: formData.titleColor || "#000000",
            descriptionColor: formData.descriptionColor || "#333333",
            formBgColor: formData.formBgColor || "#ffffff",
            formTextColor: formData.formTextColor || "#333333",
            buttonColor: formData.buttonColor || "#ffffff",
            buttonBgColor: formData.buttonBgColor || "#000000",
            buttonHoverColor: formData.buttonHoverColor || "#ffffff",
            buttonHoverBgColor: formData.buttonHoverBgColor || "#333333",
            useSectionColors: formData.useSectionColors !== false,
            useSectionFontSizes: formData.useSectionFontSizes === true,
            fontSizes: formData.fontSizes || {},
            sideContent: {
              enabled: formData.sideContent?.enabled ?? true,
              headingTop: formData.sideContent?.headingTop || "WHY CHOOSE",
              headingHighlight: formData.sideContent?.headingHighlight || "REGENCY INTERIOR",
              bullets: Array.isArray(formData.sideContent?.bullets)
                ? formData.sideContent.bullets
                : [],
              footerLine: formData.sideContent?.footerLine || "REACH OUT TODAY TO DISCUSS YOUR VISION!",
              headingColor: formData.sideContent?.headingColor || "#000000",
              highlightColor: formData.sideContent?.highlightColor || "#d4af37",
              bulletColor: formData.sideContent?.bulletColor || "#333333",
              bulletIconColor: formData.sideContent?.bulletIconColor || "#d4af37",
              footerColor: formData.sideContent?.footerColor || "#000000",
              useSectionColors: formData.sideContent?.useSectionColors !== false,
            },
          });

          // Load font styles
          setFontStyles(v?.fontStyles || DEFAULT_FONT_STYLES);
          
          // Load layout order
          setLayoutOrder((prev) => ({
            ...prev,
            ...(v.layoutOrder || {}),
            instantQuote: v.layoutOrder?.instantQuote || 10,
          }));
          
          // Load background color
          setBackgroundColor(v.backgroundColor || "#ffffff");
        }

        // Load available forms from formConfigs
        const formsSnap = await get(ref(db, "formConfigs"));
        if (formsSnap.exists()) {
          const formsData = formsSnap.val();
          const formsArray = Object.entries(formsData).map(([id, form]) => ({
            id,
            name: form.formName || "Untitled Form",
            title: form.formTitle || "",
            description: form.formDescription || "",
          }));
          setAvailableForms(formsArray);
        }
      } catch (e) {
        console.error(e);
        setError("Failed to load homepage config.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Update font style helper
  // Supports both granular updates (property/value) and full style object updates.
  const updateFontStyle = (styleType, propertyOrStyle, value) => {
    setFontStyles((prev) => {
      const base = prev[styleType] || DEFAULT_FONT_STYLES[styleType] || {};
      if (
        propertyOrStyle &&
        typeof propertyOrStyle === "object" &&
        value === undefined
      ) {
        return {
          ...prev,
          [styleType]: {
            ...base,
            ...propertyOrStyle,
          },
        };
      }

      return {
        ...prev,
        [styleType]: {
          ...base,
          [propertyOrStyle]: value,
        },
      };
    });
  };

  // upload & delete helpers
  const uploadImageFile = useCallback(async (file) => {
    if (!file) return null;
    try {
      setUploading(true);
      setError("");
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: form });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Upload failed (${res.status}) ${text}`);
      }
      const data = await res.json();
      return data?.filename || null;
    } catch (e) {
      console.error(e);
      setError(e.message || "Upload failed");
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  const deleteImageFile = useCallback(async (filename) => {
    if (!filename) return false;
    try {
      const res = await fetch(`${IMAGES_BASE}/${encodeURIComponent(filename)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
      return true;
    } catch (e) {
      console.error(e);
      setError(e.message || "Delete failed");
      return false;
    }
  }, []);

  // Process color value for saving
  const processColorValue = (value) => {
    if (value === "transparent" || value === "" || !value) {
      return "transparent";
    }
    return value;
  };

  // save
  const save = useCallback(async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const stripIds = (arr) =>
        Array.isArray(arr) ? arr.map(({ _id, ...rest }) => rest) : [];

      const payload = {
        hero: stripIds(hero),
        heroColors: {
          headingColor: processColorValue(heroColors.headingColor),
          subHeadingColor: processColorValue(heroColors.subHeadingColor),
          contentColor: processColorValue(heroColors.contentColor),
          buttonTextColor: processColorValue(heroColors.buttonTextColor),
          buttonBgColor: processColorValue(heroColors.buttonBgColor),
        },
        heroSettings: {
          useSectionColors: heroSettings.useSectionColors !== false,
          useSectionFontSizes: heroSettings.useSectionFontSizes === true,
          fontSizes: heroSettings.fontSizes || {},
        },
        
        customerreview: {
          image: customerReview.image || "",
          backgroundColor: processColorValue(customerReview.backgroundColor) || "#ffffff",
          overlayOpacity: customerReview.overlayOpacity || 0.3,
          reviews: stripIds(customerReview.reviews || []),
          sectionTitle: customerReview.sectionTitle,
          sectionDescription: customerReview.sectionDescription,
          useSectionColors: customerReview.useSectionColors !== false,
          useSectionFontSizes: customerReview.useSectionFontSizes === true,
          fontSizes: customerReview.fontSizes || {},
        },
        customerReviewColors: {
          headingColor: processColorValue(customerReviewColors.headingColor),
          subHeadingColor: processColorValue(customerReviewColors.subHeadingColor),
          contentColor: processColorValue(customerReviewColors.contentColor),
          reviewerNameColor: processColorValue(customerReviewColors.reviewerNameColor),
          reviewerDesignationColor: processColorValue(customerReviewColors.reviewerDesignationColor),
          ratingColor: processColorValue(customerReviewColors.ratingColor),
        },
        
        howWeWork: {
          display: howWeWork.display,
          sectionTitle: howWeWork.sectionTitle || "HOW WE WORK",
          sectionSubtitle: howWeWork.sectionSubtitle || "",
          backgroundColor: processColorValue(howWeWork.backgroundColor) || "#111111",
          titleColor: processColorValue(howWeWork.titleColor) || "#d4af37",
          subtitleColor: processColorValue(howWeWork.subtitleColor) || "rgba(255, 255, 255, 0.8)",
          cardBackground: processColorValue(howWeWork.cardBackground) || "#ffffff",
          numberColor: processColorValue(howWeWork.numberColor) || "#d4af37",
          textColor: processColorValue(howWeWork.textColor) || "#111111",
          useSectionColors: howWeWork.useSectionColors !== false,
          useSectionFontSizes: howWeWork.useSectionFontSizes === true,
          fontSizes: howWeWork.fontSizes || {},
          steps: howWeWork.steps || []
        },
        howWeWorkColors: {
          headingColor: processColorValue(howWeWorkColors.headingColor),
          subHeadingColor: processColorValue(howWeWorkColors.subHeadingColor),
          contentColor: processColorValue(howWeWorkColors.contentColor),
          numberColor: processColorValue(howWeWorkColors.numberColor),
          cardBgColor: processColorValue(howWeWorkColors.cardBgColor),
        },
        
        services: stripIds(services),
        servicesColors: {
          headingColor: processColorValue(servicesColors.headingColor),
          subHeadingColor: processColorValue(servicesColors.subHeadingColor),
          serviceTitleColor: processColorValue(servicesColors.serviceTitleColor),
          serviceContentColor: processColorValue(servicesColors.serviceContentColor),
        },
        servicesSettings: {
          useSectionColors: servicesSettings.useSectionColors !== false,
          useSectionFontSizes: servicesSettings.useSectionFontSizes === true,
          fontSizes: servicesSettings.fontSizes || {},
        },
        
        servicesSection: {
          title: servicesSection.title || "",
          subtitle: servicesSection.subtitle || "",
          useSectionColors: servicesSection.useSectionColors !== false,
          useSectionFontSizes: servicesSection.useSectionFontSizes === true,
          fontSizes: servicesSection.fontSizes || {},
        },
        servicesSectionColors: {
          headingColor: processColorValue(servicesSectionColors.headingColor),
          subHeadingColor: processColorValue(servicesSectionColors.subHeadingColor),
        },

        specialCategorySection: {
          useSectionColors: specialCategorySection.useSectionColors !== false,
          useSectionFontSizes: specialCategorySection.useSectionFontSizes === true,
          fontSizes: specialCategorySection.fontSizes || {},
        },
        specialCategoryColors: {
          headingColor: processColorValue(specialCategoryColors.headingColor),
          subHeadingColor: processColorValue(specialCategoryColors.subHeadingColor),
          contentColor: processColorValue(specialCategoryColors.contentColor),
        },

        categorySection: {
          useSectionColors: categorySection.useSectionColors !== false,
          useSectionFontSizes: categorySection.useSectionFontSizes === true,
          fontSizes: categorySection.fontSizes || {},
        },
        categoryColors: {
          headingColor: processColorValue(categoryColors.headingColor),
          subHeadingColor: processColorValue(categoryColors.subHeadingColor),
          contentColor: processColorValue(categoryColors.contentColor),
        },

        latestSection: {
          useSectionColors: latestSection.useSectionColors !== false,
          useSectionFontSizes: latestSection.useSectionFontSizes === true,
          fontSizes: latestSection.fontSizes || {},
        },
        latestColors: {
          headingColor: processColorValue(latestColors.headingColor),
          subHeadingColor: processColorValue(latestColors.subHeadingColor),
          contentColor: processColorValue(latestColors.contentColor),
        },

        updatesSection: {
          useSectionColors: updatesSection.useSectionColors !== false,
          useSectionFontSizes: updatesSection.useSectionFontSizes === true,
          fontSizes: updatesSection.fontSizes || {},
        },
        updatesColors: {
          headingColor: processColorValue(updatesColors.headingColor),
          subHeadingColor: processColorValue(updatesColors.subHeadingColor),
          contentColor: processColorValue(updatesColors.contentColor),
          buttonTextColor: processColorValue(updatesColors.buttonTextColor),
        },

        aboutUs: {
          display: aboutUs.display,
          title: aboutUs.title || "",
          subtitle: aboutUs.subtitle || "",
          description: aboutUs.description || "",
          image: aboutUs.image || "",
          yearsExperience: aboutUs.yearsExperience || "",
          projectsCompleted: aboutUs.projectsCompleted || "",
          buttonText: aboutUs.buttonText || "",
          buttonLink: aboutUs.buttonLink || "",
          backgroundColor: processColorValue(aboutUs.backgroundColor) || "#ffffff",
          useSectionColors: aboutUs.useSectionColors !== false,
          useSectionFontSizes: aboutUs.useSectionFontSizes === true,
          fontSizes: aboutUs.fontSizes || {},
        },
        aboutUsColors: {
          headingColor: processColorValue(aboutUsColors.headingColor),
          subHeadingColor: processColorValue(aboutUsColors.subHeadingColor),
          contentColor: processColorValue(aboutUsColors.contentColor),
          statsColor: processColorValue(aboutUsColors.statsColor),
          buttonTextColor: processColorValue(aboutUsColors.buttonTextColor),
          buttonBgColor: processColorValue(aboutUsColors.buttonBgColor),
        },

        instantQuoteForm: {
          display: instantQuoteForm.display,
          formId: instantQuoteForm.formId,
          position: instantQuoteForm.position,
          sectionTitle: instantQuoteForm.sectionTitle,
          sectionDescription: instantQuoteForm.sectionDescription,
          backgroundImage: instantQuoteForm.backgroundImage,
          backgroundColor: processColorValue(instantQuoteForm.backgroundColor),
          overlayOpacity: instantQuoteForm.overlayOpacity,
          titleColor: processColorValue(instantQuoteForm.titleColor),
          descriptionColor: processColorValue(instantQuoteForm.descriptionColor),
          formBgColor: processColorValue(instantQuoteForm.formBgColor),
          formTextColor: processColorValue(instantQuoteForm.formTextColor),
          buttonColor: processColorValue(instantQuoteForm.buttonColor),
          buttonBgColor: processColorValue(instantQuoteForm.buttonBgColor),
          buttonHoverColor: processColorValue(instantQuoteForm.buttonHoverColor),
          buttonHoverBgColor: processColorValue(instantQuoteForm.buttonHoverBgColor),
          useSectionColors: instantQuoteForm.useSectionColors !== false,
          useSectionFontSizes: instantQuoteForm.useSectionFontSizes === true,
          fontSizes: instantQuoteForm.fontSizes || {},
          sideContent: {
            enabled: instantQuoteForm.sideContent?.enabled ?? true,
            headingTop: instantQuoteForm.sideContent?.headingTop || "WHY CHOOSE",
            headingHighlight: instantQuoteForm.sideContent?.headingHighlight || "REGENCY INTERIOR",
            bullets: Array.isArray(instantQuoteForm.sideContent?.bullets)
              ? instantQuoteForm.sideContent.bullets
              : [],
            footerLine: instantQuoteForm.sideContent?.footerLine || "REACH OUT TODAY TO DISCUSS YOUR VISION!",
            headingColor: processColorValue(instantQuoteForm.sideContent?.headingColor),
            highlightColor: processColorValue(instantQuoteForm.sideContent?.highlightColor),
            bulletColor: processColorValue(instantQuoteForm.sideContent?.bulletColor),
            bulletIconColor: processColorValue(instantQuoteForm.sideContent?.bulletIconColor),
            footerColor: processColorValue(instantQuoteForm.sideContent?.footerColor),
            useSectionColors: instantQuoteForm.sideContent?.useSectionColors !== false,
          },
        },
        
        fontStyles: fontStyles,
        layoutOrder: { ...layoutOrder },
        backgroundColor: processColorValue(backgroundColor),
      };

      await set(ref(db, "homepageconfig"), payload);

      setHero((prev) => normalizeList(stripIds(prev)));
      setCustomerReview((prev) => ({
        ...prev,
        reviews: normalizeList(stripIds(prev.reviews || [])),
      }));
      setServices((prev) => normalizeList(stripIds(prev)));
      setSuccess("Saved homepage config ✔");
    } catch (e) {
      console.error(e);
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [
    hero,
    heroColors,
    heroSettings,
    customerReview,
    customerReviewColors,
    howWeWork,
    howWeWorkColors,
    services,
    servicesColors,
    servicesSettings,
    servicesSection,
    servicesSectionColors,
    specialCategorySection,
    specialCategoryColors,
    categorySection,
    categoryColors,
    latestSection,
    latestColors,
    updatesSection,
    updatesColors,
    aboutUs,
    aboutUsColors,
    instantQuoteForm,
    fontStyles,
    layoutOrder,
    backgroundColor,
  ]);

  if (loading) return <div className="hcfg-loading">Loading…</div>;

  return (
    <section className="hcfg-wrap">
      <h2 className="hcfg-title">Home Page Configuration</h2>

      {/* Global Font Styles Configuration */}
      <FontStylesConfiguration 
        fontStyles={fontStyles} 
        updateFontStyle={updateFontStyle} 
      />

      {/* Layout Settings */}
      <LayoutSettings
        backgroundColor={backgroundColor}
        setBackgroundColor={setBackgroundColor}
        layoutOrder={layoutOrder}
        setLayoutOrder={setLayoutOrder}
      />

      {/* Instant Quote Form */}
      <InstantQuoteForm
        instantQuoteForm={instantQuoteForm}
        setInstantQuoteForm={setInstantQuoteForm}
        availableForms={availableForms}
        uploadImageFile={uploadImageFile}
        deleteImageFile={deleteImageFile}
        IMAGES_BASE={IMAGES_BASE}
      />

      {/* Hero */}
      <RegularSection
        title="Hero Slides"
        list={hero}
        setList={setHero}
        isHeroSection={true}
        hasImageUpload={true}
        fields={[
          { key: "title", label: "Title", placeholder: "Hero title" },
          { key: "content", label: "Content", placeholder: "Hero description", type: "textarea", rows: 3 },
          { key: "buttonname", label: "Button name", placeholder: "Explore" },
          { key: "buttonlink", label: "Button link", placeholder: "/designs" },
        ]}
        uploadImageFile={uploadImageFile}
        deleteImageFile={deleteImageFile}
        imagesBase={IMAGES_BASE}
        sectionColors={heroColors}
        setSectionColors={setHeroColors}
        sectionSettings={{ 
          useSectionColors: heroSettings.useSectionColors,
          setUseSectionColors: (value) => setHeroSettings(prev => ({ ...prev, useSectionColors: value }))
        }}
        fontSizes={heroSettings.fontSizes || {}}
        onFontSizeChange={(key, value) =>
          setHeroSettings((prev) => ({
            ...prev,
            fontSizes: {
              ...(prev.fontSizes || {}),
              [key]: value,
            },
          }))
        }
        useSectionFontSizes={heroSettings.useSectionFontSizes === true}
        onToggleSectionFontSizes={(value) =>
          setHeroSettings((prev) => ({ ...prev, useSectionFontSizes: value }))
        }
        fontSizeFields={[
          { key: "heading", label: "Heading Size", placeholder: "e.g., 56px" },
          { key: "content", label: "Content Size", placeholder: "e.g., 18px" },
          { key: "button", label: "Button Size", placeholder: "e.g., 12px" },
        ]}
      />

      {/* About Us */}
      <AboutUsSection
        aboutUs={aboutUs}
        setAboutUs={setAboutUs}
        aboutUsColors={aboutUsColors}
        setAboutUsColors={setAboutUsColors}
        uploadImageFile={uploadImageFile}
        deleteImageFile={deleteImageFile}
        IMAGES_BASE={IMAGES_BASE}
      />

      {/* Customer Reviews */}
      <CustomerReviewSection
        title="Customer Reviews"
        data={customerReview}
        setData={setCustomerReview}
        customerReviewColors={customerReviewColors}
        setCustomerReviewColors={setCustomerReviewColors}
        uploadImageFile={uploadImageFile}
        deleteImageFile={deleteImageFile}
        imagesBase={IMAGES_BASE}
      />

      {/* How We Work Section */}
      <HowWeWorkSection
        howWeWork={howWeWork}
        setHowWeWork={setHowWeWork}
        howWeWorkColors={howWeWorkColors}
        setHowWeWorkColors={setHowWeWorkColors}
      />

      {/* Services Section Header */}
      <ServicesSectionHeader
        servicesSection={servicesSection}
        setServicesSection={setServicesSection}
        servicesSectionColors={servicesSectionColors}
        setServicesSectionColors={setServicesSectionColors}
      />

      {/* Services List */}
      <RegularSection
        title="Services"
        list={services}
        setList={setServices}
        hasImageUpload={true}
        fields={[
          { key: "title", label: "Service Title", placeholder: "e.g., Modular Kitchen" },
          {
            key: "description",
            label: "Service Description",
            placeholder: "Describe the service",
            type: "textarea",
            rows: 3,
          },
          { key: "link", label: "Link (optional)", placeholder: "/gallery/kitchen/modular" },
        ]}
        uploadImageFile={uploadImageFile}
        deleteImageFile={deleteImageFile}
        imagesBase={IMAGES_BASE}
        sectionColors={servicesColors}
        setSectionColors={setServicesColors}
        sectionSettings={{ 
          useSectionColors: servicesSettings.useSectionColors,
          setUseSectionColors: (value) => setServicesSettings(prev => ({ ...prev, useSectionColors: value }))
        }}
        fontSizes={servicesSettings.fontSizes || {}}
        onFontSizeChange={(key, value) =>
          setServicesSettings((prev) => ({
            ...prev,
            fontSizes: {
              ...(prev.fontSizes || {}),
              [key]: value,
            },
          }))
        }
        useSectionFontSizes={servicesSettings.useSectionFontSizes === true}
        onToggleSectionFontSizes={(value) =>
          setServicesSettings((prev) => ({ ...prev, useSectionFontSizes: value }))
        }
        fontSizeFields={[
          { key: "subHeading", label: "Service Title Size", placeholder: "e.g., 18px" },
          { key: "content", label: "Service Description Size", placeholder: "e.g., 14px" },
        ]}
      />

      {/* Nav-driven Sections Styling */}
      <SectionStyleCard
        title="Design Ideas (Special Category)"
        sectionSettings={specialCategorySection}
        setSectionSettings={setSpecialCategorySection}
        sectionColors={specialCategoryColors}
        setSectionColors={setSpecialCategoryColors}
        fontSizeFields={[
          { key: "heading", label: "Title Size", placeholder: "e.g., 36px" },
          { key: "subHeading", label: "Description Size", placeholder: "e.g., 14px" },
          { key: "content", label: "Tabs/Overlay Text Size", placeholder: "e.g., 12px" },
        ]}
      />

      <SectionStyleCard
        title="Category Grid"
        sectionSettings={categorySection}
        setSectionSettings={setCategorySection}
        sectionColors={categoryColors}
        setSectionColors={setCategoryColors}
        fontSizeFields={[
          { key: "heading", label: "Title Size", placeholder: "e.g., 34px" },
          { key: "subHeading", label: "Description Size", placeholder: "e.g., 14px" },
          { key: "content", label: "Tile Title Size", placeholder: "e.g., 12px" },
        ]}
      />

      <SectionStyleCard
        title="Latest Projects"
        sectionSettings={latestSection}
        setSectionSettings={setLatestSection}
        sectionColors={latestColors}
        setSectionColors={setLatestColors}
        fontSizeFields={[
          { key: "heading", label: "Title Size", placeholder: "e.g., 34px" },
          { key: "subHeading", label: "Subtitle Size", placeholder: "e.g., 14px" },
          { key: "content", label: "Card Title Size", placeholder: "e.g., 12px" },
        ]}
      />

      <SectionStyleCard
        title="Updates / Trending Posts"
        sectionSettings={updatesSection}
        setSectionSettings={setUpdatesSection}
        sectionColors={updatesColors}
        setSectionColors={setUpdatesColors}
        fontSizeFields={[
          { key: "heading", label: "Title Size", placeholder: "e.g., 34px" },
          { key: "subHeading", label: "Description Size", placeholder: "e.g., 14px" },
          { key: "content", label: "Card Title Size", placeholder: "e.g., 12px" },
          { key: "button", label: "Link/Button Size", placeholder: "e.g., 12px" },
        ]}
      />

      {uploading && <p className="hcfg-info">Uploading image…</p>}
      {error && <p className="hcfg-error">{error}</p>}
      {success && <p className="hcfg-success">{success}</p>}

      <div className="hcfg-actions">
        <button 
          className="hcfg-btn primary" 
          onClick={save} 
          disabled={saving}
          style={{
            padding: "12px 24px",
            background: "#4CAF50",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "bold"
          }}
        >
          {saving ? "Saving…" : "Save Homepage Config"}
        </button>
      </div>
    </section>
  );
}
